/**
 * Route <-> label mapping for the App Shell Left Nav. `LEFT_NAV_ITEMS`
 * The App Shell mounts under `<BrowserRouter>`
 * with no basename, so router paths and browser URLs are identical
 * (`/search`, `/baseline`, `/validation`, `/launch`).
 */

export type LeftNavPath = "/search" | "/baseline" | "/validation" | "/launch";

/** Validation Dashboard tab identifiers (R6.1). */
export type ValidationTabId =
  | "dashboard"
  | "step-breakdown"
  | "program-tickets"
  | "catalog-issues";

/** Default path for unknown / missing suffixes (R1.9). */
export const DEFAULT_LEFT_NAV_PATH: LeftNavPath = "/search";

/**
 * Ordered list of primary Left Nav items. Consumers that iterate the
 * Left Nav MUST iterate this array so the rendered order matches R1.2.
 */
export const LEFT_NAV_ITEMS: ReadonlyArray<{
  readonly path: LeftNavPath;
  readonly label: string;
}> = [
  { path: "/search", label: "ASIN Search" },
  { path: "/baseline", label: "ASIN Date Baseline" },
  { path: "/validation", label: "Validation" },
  { path: "/launch", label: "Launch" },
];

/** Path -> label lookup, derived so the two never drift. */
export const LEFT_NAV_LABELS: Readonly<Record<LeftNavPath, string>> =
  LEFT_NAV_ITEMS.reduce((acc, item) => {
    acc[item.path] = item.label;
    return acc;
  }, {} as Record<LeftNavPath, string>);

const KNOWN_PATHS: ReadonlySet<string> = new Set(
  LEFT_NAV_ITEMS.map((item) => item.path)
);

/**
 * Match the first path segment of `suffix` against `LEFT_NAV_ITEMS`,
 * falling back to `DEFAULT_LEFT_NAV_PATH` for empty / `/` / unknown
 * suffixes. Handles bare (`"search"`), leading-slash (`"/search"`),
 * and long-form (`"/search/details/..."`) inputs uniformly.
 */
export function resolveLeftNavPath(suffix: string): LeftNavPath {
  const firstSegment = suffix.replace(/^\/+/, "").split("/")[0];
  const candidate = `/${firstSegment}`;
  if (KNOWN_PATHS.has(candidate)) {
    return candidate as LeftNavPath;
  }
  return DEFAULT_LEFT_NAV_PATH;
}
